/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Copy, Check, Loader2, AlertCircle, Terminal, FileText } from 'lucide-react';
import {
  getMcpConfigSnippet,
  getSkillInstallInstructions,
  getAgentDisplayName,
  type InstallableAgent,
  type ConfigSnippet,
  type SkillInstallInstructions,
} from '@/services/skillshop';

interface InstallCopyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'mcp' | 'skill';
  itemId: string;
  itemName: string;
  agent: InstallableAgent;
  error?: string | null;
}

/**
 * Dialog for manual installation when auto-install fails
 * Shows copyable config snippet for MCP servers or shell commands for Skills
 */
export function InstallCopyDialog({
  open,
  onOpenChange,
  type,
  itemId,
  itemName,
  agent,
  error,
}: InstallCopyDialogProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const agentDisplayName = getAgentDisplayName(agent);

  // Fetch config/instructions when dialog opens
  const { data: mcpConfig, isLoading: isLoadingMcp } = useQuery({
    queryKey: ['install-config', 'mcp', itemId, agent],
    queryFn: () => getMcpConfigSnippet(itemId, agent),
    enabled: open && type === 'mcp',
    staleTime: 5 * 60 * 1000,
  });

  const { data: skillInstructions, isLoading: isLoadingSkill } = useQuery({
    queryKey: ['install-config', 'skill', itemId, agent],
    queryFn: () => getSkillInstallInstructions(itemId, agent),
    enabled: open && type === 'skill',
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = type === 'mcp' ? isLoadingMcp : isLoadingSkill;

  // Reset copied state when dialog closes
  useEffect(() => {
    if (!open) {
      setCopied(false);
    }
  }, [open]);

  const handleCopy = async () => {
    let textToCopy = '';
    
    if (type === 'mcp' && mcpConfig) {
      textToCopy = mcpConfig.snippet;
    } else if (type === 'skill' && skillInstructions) {
      textToCopy = skillInstructions.instructions;
    }

    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast({
        title: 'Copied to Clipboard',
        description: type === 'mcp' 
          ? 'Paste this into your agent config file.'
          : 'Run these commands in your terminal.',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: 'Copy Failed',
        description: 'Please manually select and copy the text.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {type === 'mcp' ? (
              <FileText className="h-5 w-5 text-blue-600" />
            ) : (
              <Terminal className="h-5 w-5 text-green-600" />
            )}
            Manual Installation
          </DialogTitle>
          <DialogDescription>
            {type === 'mcp' 
              ? `Add this configuration to your ${agentDisplayName} settings.`
              : `Run these commands to install the skill to ${agentDisplayName}.`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Error message if present */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-700">
              <p className="font-medium">Auto-install failed</p>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* MCP Config Content */}
        {type === 'mcp' && mcpConfig && !isLoading && (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                {mcpConfig.instructions}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                {mcpConfig.configPath}
              </p>
            </div>
            <div className="relative">
              <pre className="p-4 bg-slate-900 text-slate-50 rounded-lg text-sm overflow-x-auto">
                <code>{mcpConfig.snippet}</code>
              </pre>
            </div>
          </div>
        )}

        {/* Skill Instructions Content */}
        {type === 'skill' && skillInstructions && !isLoading && (
          <div className="space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Copy and run these commands in your terminal:
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">
                  {skillInstructions.targetDir}
                </span>
              </div>
            </div>
            <div className="relative">
              <pre className="p-4 bg-slate-900 text-slate-50 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap">
                <code>{skillInstructions.instructions}</code>
              </pre>
            </div>
            {/* Show files that will be installed */}
            {skillInstructions.files && skillInstructions.files.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Files included:</p>
                <div className="flex flex-wrap gap-1">
                  {skillInstructions.files.slice(0, 5).map((file) => (
                    <span 
                      key={file.relativePath} 
                      className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono"
                    >
                      {file.name}
                    </span>
                  ))}
                  {skillInstructions.files.length > 5 && (
                    <span className="text-xs text-muted-foreground">
                      +{skillInstructions.files.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button 
            onClick={handleCopy}
            disabled={isLoading || (!mcpConfig && !skillInstructions)}
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy {type === 'mcp' ? 'Config' : 'Commands'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
